// src/components/AppInput/AppInputFileMultiple.tsx
import React, { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import { blobToFile } from 'utils/blobToFile'
import { convertWebPToJpeg } from 'utils/convertWebPToJpeg'

enum AppInputFileView {
  link = 'link',
}

export enum AppInputFileColor {
  violet = '',
  gray = 'gray',
}

const options = {
  maxSizeMB: 0.5, // Максимальный размер 0.5 MB
  maxWidthOrHeight: 800, // Уменьшение размера изображения
  useWebWorker: true,
}

interface IAppInputFileProps {
  color?: AppInputFileColor
  text?: string
  view?: AppInputFileView
  onChangeFile: (files: File[]) => void
  setLoading?: (loading: boolean) => void
  setLoadingConversion?: (loading: boolean) => void
  setFilePreview?: (values: string[]) => void
  extraText?: string
  multiple?: boolean
}

// проверка изображение или нет
const isImage = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  return validTypes.includes(file.type)
}

export const AppInputFileMultiple = ({
  color = AppInputFileColor.violet,
  view = AppInputFileView.link,
  text,
  onChangeFile,
  setLoading,
  setLoadingConversion,
  setFilePreview,
  extraText,
  multiple,
}: IAppInputFileProps) => {
  const { t } = useTranslation()

  const changeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return

    setFilePreview?.([])

    const selectedFiles = Array.from(fileList)

    const needsConversion = selectedFiles.some(
      (f) => f.name.toLowerCase().endsWith('.heic') || f.type === 'image/webp',
    )

    try {
      setLoading?.(true)
      if (needsConversion) setLoadingConversion?.(true)

      const processedFiles: File[] = []

      for (const original of selectedFiles) {
        let file = original
        try {
          // HEIC -> JPEG
          if (file.name.toLowerCase().endsWith('.heic')) {
            const imageBlob = (await heic2any({
              blob: file,
              toType: 'image/jpeg',
            })) as Blob
            file = blobToFile(imageBlob, file.name.replace(/\.heic$/i, '.jpg'))
          }

          // WebP -> JPEG
          if (file.type === 'image/webp') {
            file = await convertWebPToJpeg(file)
          }

          if (isImage(file)) {
            const compressedFile = await imageCompression(file, options)
            processedFiles.push(compressedFile as File)
          } else {
            processedFiles.push(file)
          }
        } catch (error) {
          console.error('Ошибка обработки файла:', file.name, error)

          processedFiles.push(original)
        }
      }

      onChangeFile(processedFiles)
    } catch (error) {
      console.error('Ошибка общего процесса обработки файлов:', error)

      onChangeFile(Array.from(fileList))
    } finally {
      if (needsConversion) setLoadingConversion?.(false)
      setLoading?.(false)

      e.target.value = ''
    }
  }

  return (
    <>
      <label className={`file-input-label ${color}`}>
        {t(text || 'выберите файл ')}
        <input
          type="file"
          className="file-input"
          onChange={changeHandler}
          multiple={multiple}
        />
      </label>
      {extraText && <span>{extraText}</span>}
    </>
  )
}
